import React, { useEffect, useRef, useState } from 'react';
import { Smile } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import data from '@emoji-mart/data';
import { Picker } from 'emoji-mart';

export interface MentionUser {
    id: number | string;
    name: string;
}

export interface Mention {
    userId: number | string;
    userName: string;
}

export type MessageBoxProps =
    Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange" | "value"> & {
    value?: string;
    onChange?: (value: string) => void;
    maxLength?: number;
    users?: MentionUser[];
    onMentionsChange?: (mentions: Mention[]) => void;
};

interface MentionDropdownState {
    active: boolean;
    query: string;
    startIndex: number;
}

export const MessageBox = ({
                               className,
                               maxLength = 500,
                               value = "",
                               onChange,
                               users = [],
                               onMentionsChange,
                               ...props
                           }: MessageBoxProps) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const pickerRef = useRef<HTMLDivElement>(null);
    const mentionDropdownRef = useRef<HTMLDivElement>(null);

    const [isEmojiOpen, setIsEmojiOpen] = useState(false);
    const [mentionDropdown, setMentionDropdown] = useState<MentionDropdownState>({
        active: false,
        query: "",
        startIndex: -1,
    });
    const [highlightedMentionIndex, setHighlightedMentionIndex] = useState(0);
    const [activeMentions, setActiveMentions] = useState<Mention[]>([]);

    const filteredMentionUsers = mentionDropdown.active
        ? users.filter(u => u.name.toLowerCase().includes(mentionDropdown.query.toLowerCase())).slice(0, 5)
        : [];

    useEffect(() => {
        onMentionsChange?.(activeMentions);
    }, [activeMentions]);

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = event.target.value;
        onChange?.(newValue);

        const textarea = event.target;
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;

        const cursor = textarea.selectionStart ?? 0;
        const textBeforeCursor = newValue.slice(0, cursor);
        const lastAtIndex = textBeforeCursor.lastIndexOf("@");

        if (lastAtIndex !== -1) {
            const textAfterAt = textBeforeCursor.slice(lastAtIndex + 1);
            if (!textAfterAt.includes(" ")) {
                setMentionDropdown({ active: true, query: textAfterAt, startIndex: lastAtIndex });
                setHighlightedMentionIndex(0);
                return;
            }
        }

        setMentionDropdown({ active: false, query: "", startIndex: -1 });
    };

    const insertMention = (user: MentionUser) => {
        const textarea = textareaRef.current;
        if (!textarea) {
            return;
        }

        const textBeforeMention = value.slice(0, mentionDropdown.startIndex);
        const textAfterMention = value.slice(mentionDropdown.startIndex + 1 + mentionDropdown.query.length);
        const mentionText = `@${user.name}`;
        const newValue = `${textBeforeMention}${mentionText} ${textAfterMention}`;

        onChange?.(newValue);
        setActiveMentions(previous =>
            previous.find(mention => mention.userId === user.id)
                ? previous
                : [...previous, { userId: user.id, userName: user.name }]
        );
        setMentionDropdown({ active: false, query: "", startIndex: -1 });

        setTimeout(() => {
            textarea.focus();
            const newCaretPosition = textBeforeMention.length + mentionText.length + 1;
            textarea.setSelectionRange(newCaretPosition, newCaretPosition);
        }, 0);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (!mentionDropdown.active || filteredMentionUsers.length === 0) {
            return;
        }

        if (event.key === "ArrowDown") {
            event.preventDefault();
            setHighlightedMentionIndex(index => (index + 1) % filteredMentionUsers.length);
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setHighlightedMentionIndex(index => (index - 1 + filteredMentionUsers.length) % filteredMentionUsers.length);
        } else if (event.key === "Enter" || event.key === "Tab") {
            event.preventDefault();
            insertMention(filteredMentionUsers[highlightedMentionIndex]);
        } else if (event.key === "Escape") {
            setMentionDropdown({ active: false, query: "", startIndex: -1 });
        }
    };

    const insertEmoji = (emojiNative: string) => {
        onChange?.(String(value ?? "") + emojiNative);
        textareaRef.current?.focus();
    };

    useEffect(() => {
        if (!isEmojiOpen || !pickerRef.current) {
            return;
        }
        pickerRef.current.innerHTML = "";
        const picker = new Picker({
            data,
            theme: "light",
            previewPosition: "none",
            onEmojiSelect: (emoji: { native: string }) => {
                insertEmoji(emoji.native);
                setIsEmojiOpen(false);
            }
        });
        pickerRef.current.appendChild(picker as unknown as Node);
        return () => {
            if (pickerRef.current) {
                pickerRef.current.innerHTML = "";
            }
        };
    }, [isEmojiOpen]);

    useEffect(() => {
        if (!isEmojiOpen) {
            return;
        }
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsEmojiOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isEmojiOpen]);

    useEffect(() => {
        if (!mentionDropdown.active) {
            return;
        }
        const handleClickOutside = (event: MouseEvent) => {
            if (
                mentionDropdownRef.current && !mentionDropdownRef.current.contains(event.target as Node) &&
                textareaRef.current && !textareaRef.current.contains(event.target as Node)
            ) {
                setMentionDropdown({ active: false, query: "", startIndex: -1 });
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [mentionDropdown.active]);

    const baseStyles = "w-full bg-gray-100 rounded-lg p-3 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500 hover:ring-2 hover:ring-blue-500 transition-shadow text-center overflow-hidden resize-none placeholder:text-gray-400";

    return (
        <div>
            <div className="relative" ref={wrapperRef}>
                <textarea
                    {...props}
                    ref={textareaRef}
                    className={`${baseStyles} ${className || ''}`}
                    value={value}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                />

                <button
                    type="button"
                    aria-label="Add emoji"
                    onClick={() => setIsEmojiOpen(isOpen => !isOpen)}
                    className="absolute right-3 bottom-3 text-gray-400 hover:text-gray-500 transition-colors"
                >
                    <Smile className="size-5" />
                </button>

                <AnimatePresence>
                    {mentionDropdown.active && filteredMentionUsers.length > 0 && (
                        <motion.div
                            ref={mentionDropdownRef}
                            initial={{ opacity: 0, y: -4, scale: 0.97 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -4, scale: 0.97 }}
                            transition={{ duration: 0.1 }}
                            className="absolute bottom-2 right-10 w-56 rounded-lg border border-gray-100 bg-white shadow-lg ring-1 ring-black/5 z-50 overflow-hidden"
                        >
                            <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                                People
                            </p>
                            {filteredMentionUsers.map((user, index) => (
                                <button
                                    key={user.id}
                                    type="button"
                                    onMouseDown={event => {
                                        event.preventDefault();
                                        insertMention(user);
                                    }}
                                    onMouseEnter={() => setHighlightedMentionIndex(index)}
                                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm transition-colors ${
                                        index === highlightedMentionIndex
                                            ? "bg-blue-50 text-blue-700"
                                            : "text-gray-700 hover:bg-gray-50"
                                    }`}
                                >
                                    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-orange-400 text-[11px] font-semibold text-white">
                                        {user.name.charAt(0).toUpperCase()}
                                    </span>
                                    <span className="truncate font-medium">{user.name}</span>
                                </button>
                            ))}
                            <div className="h-1.5" />
                        </motion.div>
                    )}
                </AnimatePresence>

                <AnimatePresence>
                    {isEmojiOpen && (
                        <motion.div
                            initial={{ opacity: 0, y: 8, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 8, scale: 0.98 }}
                            transition={{ duration: 0.18 }}
                            className="absolute right-0 top-full mt-2 z-50"
                        >
                            <div className="bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                                <div ref={pickerRef} />
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>

            {String(value).length > 0 && (
                <div className="text-center text-sm text-gray-400 mt-2">
                    {String(value).length}/{maxLength} characters
                </div>
            )}
        </div>
    );
};